import * as React from "react";
import { Namespace, useT, useDateFnsLocale } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { toTimestamp, fromTimestamp } from "@racoon/lib";
import { skipToken } from "@reduxjs/toolkit/query";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, ArrowDown } from "lucide-react";
import { useListRoutesQuery } from "~/shared/api/routes/route.grpc";
import { useListDriverOnShiftQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useTableExport } from "@racoon/ui";

interface ShiftRow {
  shiftId: string;
  date: Date | null;
  routesCompleted: number;
  reward: number;
}

const COLUMN_KEYS = [
  "shift.col.id",
  "shift.col.date",
  "shift.col.routes",
  "shift.col.reward",
] as const;

const isCompleted = (status?: ConnectProto.RouteStatus) =>
  status === ConnectProto.RouteStatus.COMPLETED ||
  status === ConnectProto.RouteStatus.FORCE_COMPLETED;

const formatDate = (d: Date | null) => (d ? format(d, "dd.MM.yyyy") : "—");

export function ShiftHistoryTab({ user }: { user: ConnectProto.User }) {
  const t = useT();
  const dateFnsLocale = useDateFnsLocale();

  const [month, setMonth] = React.useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const monthEnd = React.useMemo(() => {
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [month]);

  // Shifts are the source of truth — a shift with no routes still has to show.
  const { data: shiftsData, isLoading: shiftsLoading } = useListDriverOnShiftQuery(
    user.id && user.ownerOrganizationId
      ? {
          ownerOrganizationIds: [user.ownerOrganizationId],
          // No listFilter: a pagination context would clamp the month to 100 rows.
          dateTimeFilter: {
            from: toTimestamp(month),
            to: toTimestamp(monthEnd),
            field: "tw_start_at",
          },
        }
      : skipToken,
  );
  // The list endpoint has no driver filter yet, so narrow to this driver here.
  const shifts = React.useMemo(
    () => (shiftsData?.driverOnShifts ?? []).filter((s) => String(s.driverId) === String(user.id)),
    [shiftsData, user.id],
  );

  const { data: routesData, isLoading: routesLoading } = useListRoutesQuery(
    shifts.length > 0 && user.ownerOrganizationId
      ? {
          ownerOrganizationIds: [user.ownerOrganizationId],
          driverOnShiftIds: shifts.map((s) => BigInt(s.id)),
        }
      : skipToken,
  );

  const rows = React.useMemo<ShiftRow[]>(() => {
    const byShift = new Map<string, ShiftRow>();
    for (const shift of shifts) {
      byShift.set(String(shift.id), {
        shiftId: `S${shift.id}`,
        date: fromTimestamp(shift.twStartAt) ?? fromTimestamp(shift.createdAt),
        routesCompleted: 0,
        reward: 0,
      });
    }
    for (const route of routesData?.routes ?? []) {
      const row = byShift.get(String(route.driverOnShiftId));
      if (!row || !isCompleted(route.status)) continue;
      row.routesCompleted += 1;
      row.reward += Number(route.reward ?? 0);
    }
    return [...byShift.values()].sort(
      (a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0),
    );
  }, [shifts, routesData]);

  const isLoading = shiftsLoading || (shifts.length > 0 && routesLoading);

  const totals = React.useMemo(
    () => ({
      shifts: rows.length,
      routes: rows.reduce((s, r) => s + r.routesCompleted, 0),
      reward: rows.reduce((s, r) => s + r.reward, 0),
    }),
    [rows],
  );

  const monthLabel = React.useMemo(() => {
    const label = format(month, "LLLL yyyy", { locale: dateFnsLocale });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }, [month, dateFnsLocale]);

  const shiftMonth = (dir: -1 | 1) =>
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() + dir, 1));

  const { handleExportExcel } = useTableExport<ShiftRow>({
    data: rows,
    columns: [
      { id: "shiftId", header: t(Namespace.Employees, "shift.col.id"), accessor: (r) => r.shiftId },
      {
        id: "date",
        header: t(Namespace.Employees, "shift.col.date"),
        accessor: (r) => formatDate(r.date),
      },
      {
        id: "routes",
        header: t(Namespace.Employees, "shift.col.routes"),
        accessor: (r) => r.routesCompleted,
      },
      {
        id: "reward",
        header: t(Namespace.Employees, "shift.col.reward"),
        accessor: (r) => `${r.reward} KZT`,
      },
    ],
    reportName: `${[user.firstName, user.lastName].filter(Boolean).join("_") || "driver"}_shifts_${monthLabel.replace(/\s/g, "_")}`,
  });

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 gap-3 overflow-hidden">
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          aria-label={t(Namespace.Employees, "shift.prevMonth")}
          onClick={() => shiftMonth(-1)}
          className="w-6 h-6 flex items-center justify-center rounded-full bg-[#f2f4f7] hover:bg-gray-200 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5 text-[#1d1f23]" />
        </button>
        <span className="text-sm text-[#1d1f23]">{monthLabel}</span>
        <button
          type="button"
          aria-label={t(Namespace.Employees, "shift.nextMonth")}
          onClick={() => shiftMonth(1)}
          className="w-6 h-6 flex items-center justify-center rounded-full bg-[#f2f4f7] hover:bg-gray-200 transition-colors"
        >
          <ChevronRight className="w-3.5 h-3.5 text-[#1d1f23]" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-[#e2e8f0]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-[#e2e8f0]">
              {COLUMN_KEYS.map((key) => (
                <th
                  key={key}
                  className="text-left font-medium text-xs uppercase text-[#64748b] px-4 py-2.5"
                >
                  {t(Namespace.Employees, key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.shiftId} className="border-b border-[#e2e8f0] last:border-b-0">
                <td className="px-4 py-2.5 text-[#1d1f23]">{row.shiftId}</td>
                <td className="px-4 py-2.5 text-[#1d1f23]">{formatDate(row.date)}</td>
                <td className="px-4 py-2.5 text-[#1d1f23]">{row.routesCompleted}</td>
                <td className="px-4 py-2.5 text-[#1d1f23]">{row.reward} KZT</td>
              </tr>
            ))}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-[#64748b]">
                  {t(Namespace.Employees, "shift.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="shrink-0 flex items-center justify-between gap-4 rounded-lg bg-[#f2f4f7] px-4 py-2 text-[13px] text-[#1d1f23]">
        <div className="flex items-center gap-8 min-w-0">
          <span>
            {t(Namespace.Employees, "shift.totalShifts")} {totals.shifts}
          </span>
          <span>
            {t(Namespace.Employees, "shift.totalRoutes")} {totals.routes}
          </span>
          <span className="truncate">
            {t(Namespace.Employees, "shift.totalReward")} {totals.reward} KZT
          </span>
        </div>
        <button
          type="button"
          onClick={handleExportExcel}
          disabled={rows.length === 0}
          className="flex items-center gap-1.5 shrink-0 text-[#1d1f23] hover:opacity-70 transition-opacity disabled:opacity-40"
        >
          <span className="w-[18px] h-[18px] rounded-full bg-btn-green flex items-center justify-center">
            <ArrowDown className="w-3 h-3 text-white" />
          </span>
          {t(Namespace.Employees, "shift.download")}
        </button>
      </div>
    </div>
  );
}
